/**
 * Organizations List Page
 * Browse and search all organizations
 */

import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { apiClient } from "../lib/api"
import { debounce, formatCurrency, formatDate, getStatusColor } from "../lib/utils"
import type { Organization } from "../types"

export function OrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState(searchParams.get("search") || "")
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "")
  const page = Number(searchParams.get("page")) || 1
  const limit = 20

  useEffect(() => {
    loadOrganizations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadOrganizations = async () => {
    setIsLoading(true)

    try {
      const result = await apiClient.listOrganizations({
        page,
        limit,
        search: search || undefined,
        status: statusFilter || undefined,
      })

      setOrganizations(result.data || [])
      setTotal(result.pagination?.total || 0)
    } catch (err) {
      console.error("Failed to load organizations:", err)
      setOrganizations([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearchChange = debounce((value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set("search", value)
    } else {
      params.delete("search")
    }
    params.set("page", "1")
    setSearchParams(params)
  }, 500)

  const handleStatusFilter = (status: string) => {
    const params = new URLSearchParams(searchParams)
    if (status) {
      params.set("status", status)
    } else {
      params.delete("status")
    }
    params.set("page", "1")
    setSearchParams(params)
    setStatusFilter(status)
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set("page", newPage.toString())
    setSearchParams(params)
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div style={{ padding: "2rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "2rem" }}>
        <div>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
            Organizations
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>{total} total organizations</p>
        </div>
        <Link to="/organizations/new">
          <button
            type="button"
            style={{
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              border: "none",
              borderRadius: "12px",
              color: "white",
              fontSize: "0.9375rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 0 20px rgba(139, 92, 246, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)"
              e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.4)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)"
              e.currentTarget.style.boxShadow = "0 0 20px rgba(139, 92, 246, 0.3)"
            }}
          >
            Create Organization
          </button>
        </Link>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search organizations..."
            defaultValue={search}
            onChange={(e) => {
              setSearch(e.target.value)
              handleSearchChange(e.target.value)
            }}
            style={{
              flex: 1,
              padding: "0.5rem 1rem",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              fontSize: "0.875rem",
            }}
          />

          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
            style={{
              padding: "0.5rem 1rem",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              fontSize: "0.875rem",
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>

          {(search || statusFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearch("")
                setStatusFilter("")
                setSearchParams(new URLSearchParams())
              }}
              style={{
                padding: "0.5rem 1rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                color: "#e2e8f0",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Organizations Table */}
      <div className="card">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>Loading organizations...</p>
          </div>
        ) : organizations.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>No organizations found</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Credit Limit</th>
                    <th>Credit Used</th>
                    <th>Account Type</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org.id}>
                      <td>
                        <Link
                          to={`/organizations/${org.id}`}
                          style={{ textDecoration: "none", color: "#a78bfa", fontWeight: 500 }}
                        >
                          {org.name}
                        </Link>
                        {org.displayName && org.displayName !== org.name && (
                          <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{org.displayName}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${getStatusColor(org.status)}`}>{org.status}</span>
                      </td>
                      <td>{formatCurrency(org.creditLimit)}</td>
                      <td>{formatCurrency(org.creditUsed)}</td>
                      <td>
                        <span className="badge badge-default">{org.accountType}</span>
                      </td>
                      <td>{formatDate(org.createdAt, { month: "short", day: "numeric", year: "numeric" })}</td>
                      <td>
                        <Link to={`/organizations/${org.id}`} style={{ textDecoration: "none" }}>
                          <button
                            type="button"
                            style={{
                              padding: "0.5rem 1rem",
                              background: "rgba(255, 255, 255, 0.05)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              borderRadius: "8px",
                              color: "#e2e8f0",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                              e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)"
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                            }}
                          >
                            View
                          </button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "1.5rem",
                  paddingTop: "1rem",
                  borderTop: "1px solid rgba(255, 255, 255, 0.05)",
                }}
              >
                <p style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                  Page {page} of {totalPages}
                </p>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    style={{
                      padding: "0.5rem 1rem",
                      background: page <= 1 ? "rgba(100, 116, 139, 0.3)" : "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: page <= 1 ? "#64748b" : "#e2e8f0",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      cursor: page <= 1 ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (page > 1) {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page > 1) {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                      }
                    }}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    style={{
                      padding: "0.5rem 1rem",
                      background: page >= totalPages ? "rgba(100, 116, 139, 0.3)" : "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: page >= totalPages ? "#64748b" : "#e2e8f0",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      cursor: page >= totalPages ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (page < totalPages) {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page < totalPages) {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                      }
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
